import React, { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import { MapContainer, ZoomControl, useMap } from 'react-leaflet';
import { OpenFreeMapLayer } from './openFreeMap.js';
import { legName, placeBox, stopName, textWidth, tripSegments } from './tripMap.js';

// The trip to work on a real map (the big view in the listing popup and the phone panel, CommuteTrip.js): the
// site's own base map (OpenFreeMap: Positron, the lifted Dark in dark mode) under the route's line in each
// leg's colour, walking dotted, with the stops, a badge per line, home and the workplace. It can be zoomed and
// panned (on phones pinch only, so a finger still scrolls the popup). Fitted to the trip, clear of what floats
// over it (`pad`). If the base map can't be shown, `fallback` (the drawing without a map) takes its place.

const HOUSE_PATH = 'M3 11l9-7 9 7v9a1 1 0 0 1-1 1h-5v-6h-6v6H4a1 1 0 0 1-1-1z';
const WORK_PATH = 'M3 9a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2zM9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2';
const ROUTE_PANE = 'tripRoutePane';
const LABEL_PANE = 'tripLabelPane';

const escapeHtml = (value) => String(value || '').replace(/[&<>"]/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[ch]));

// The leg colours are CSS tokens (var(--commute-bus)); an SVG stroke attribute needs the value itself.
const resolveColour = (value, element) => {
    const match = /^var\((--[\w-]+)\)$/.exec(String(value || '').trim());
    if (!match) return value;
    return getComputedStyle(element).getPropertyValue(match[1]).trim() || '#5f6c6a';
};

// The point halfway along a line of [lat, lng] points (by length, longitude scaled by latitude).
const halfway = (points) => {
    if (points.length < 2) return points[0];
    const k = Math.cos((points[0][0] * Math.PI) / 180);
    const lengths = points.slice(1).map((p, i) => Math.hypot(p[0] - points[i][0], (p[1] - points[i][1]) * k));
    let rest = lengths.reduce((sum, d) => sum + d, 0) / 2;
    for (let i = 0; i < lengths.length; i += 1) {
        if (rest <= lengths[i] && lengths[i] > 0) {
            const t = rest / lengths[i];
            return [points[i][0] + (points[i + 1][0] - points[i][0]) * t, points[i][1] + (points[i + 1][1] - points[i][1]) * t];
        }
        rest -= lengths[i];
    }
    return points[points.length - 1];
};

const endIcon = (kind, path, small = false) => L.divIcon({
    className: `trip-map-end trip-map-end--${kind}${small ? ' is-small' : ''}`,
    iconSize: small ? [18, 18] : [30, 30],
    iconAnchor: small ? [9, 9] : [15, 15],
    html: small ? '' : `<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linejoin="round" stroke-linecap="round" aria-hidden="true"><path d="${path}"/></svg>`,
});

const labelIcon = (className, text, style = '') => L.divIcon({
    className: 'trip-map-label-anchor',
    iconSize: [0, 0],
    html: `<span class="${className}"${style ? ` style="${style}"` : ''}>${escapeHtml(text)}</span>`,
});

// The labels' sizes, for placing them: a line's badge, a stop's name, the workplace.
const LABEL_KINDS = {
    badge: { className: 'trip-map-badge', font: '700 12px Roboto, Arial, sans-serif', padX: 16, h: 22, gap: 9 },
    work: { className: 'trip-map-work', font: '600 12px "Space Grotesk", Roboto, sans-serif', padX: 18, h: 24, gap: 18 },
    stop: { className: 'trip-map-stop', font: '600 12px Roboto, Arial, sans-serif', padX: 4, h: 16, gap: 8 },
};

const WEIGHTS = {
    full: { line: 6, casing: 10, walk: 4, stop: 5 },
    tile: { line: 4, casing: 7, walk: 3, stop: 0 },
};

function TripRoute({ route, mode, home, work, workLabel, swedish, pad, fitKey, tile }) {
    const map = useMap();
    useEffect(() => {
        [ROUTE_PANE, LABEL_PANE].forEach((name, index) => {
            if (!map.getPane(name)) map.createPane(name).style.zIndex = String(450 + index * 20);
        });
    }, [map]);

    useEffect(() => {
        const container = map.getContainer();
        const group = L.featureGroup().addTo(map);
        const labelGroup = L.layerGroup().addTo(map);
        const renderer = L.svg({ pane: ROUTE_PANE, padding: 0.5 });
        const weights = tile ? WEIGHTS.tile : WEIGHTS.full;
        const segments = tripSegments(route, mode).filter((segment) => segment.points.length > 1);
        const all = [];
        const named = new Set();
        const labels = []; // { latlng, text, kind, style }, placed after the fit (and again after a zoom)
        const obstacles = []; // [latlng, radius px]: the ends and the stops, which labels keep clear of
        segments.forEach((segment) => {
            all.push(...segment.points);
            const colour = resolveColour(segment.colour, container);
            if (segment.kind === 'walk') {
                L.polyline(segment.points, {
                    renderer, color: resolveColour('var(--commute-walk)', container), weight: weights.walk, opacity: 0.95,
                    dashArray: `1 ${weights.walk * 2}`, lineCap: 'round', interactive: false,
                }).addTo(group);
                return;
            }
            L.polyline(segment.points, { renderer, color: resolveColour('var(--trip-casing)', container), weight: weights.casing, opacity: 1, interactive: false }).addTo(group);
            L.polyline(segment.points, { renderer, color: colour, weight: weights.line, opacity: 1, interactive: false }).addTo(group);
            if (mode !== 'transit' || tile) return;
            // The line's stops at either end, and its badge halfway.
            [segment.points[0], segment.points[segment.points.length - 1]].forEach((point) => {
                L.circleMarker(point, {
                    renderer, radius: 5, color: colour, weight: 3, fillColor: resolveColour('var(--trip-casing)', container), fillOpacity: 1, interactive: false,
                }).addTo(group);
                obstacles.push([point, 7]);
            });
            const text = `${legName(segment.leg, swedish)}${segment.leg.line ? ` ${segment.leg.line}` : ''}`;
            labels.push({ latlng: halfway(segment.points), text, kind: 'badge', style: `background:${colour}` });
            [[segment.leg.from, segment.points[0]], [segment.leg.to, segment.points[segment.points.length - 1]]].forEach(([name, point]) => {
                const label = stopName(name);
                if (!label || label === 'START' || label === 'END' || named.has(label)) return;
                named.add(label);
                labels.push({ latlng: point, text: label, kind: 'stop' });
            });
        });
        if (home) {
            all.push(home);
            L.marker(home, { pane: LABEL_PANE, interactive: false, keyboard: false, icon: endIcon('home', HOUSE_PATH, tile) }).addTo(group);
            obstacles.push([home, 17]);
        }
        if (work) {
            all.push(work);
            L.marker(work, { pane: LABEL_PANE, interactive: false, keyboard: false, icon: endIcon('work', WORK_PATH, tile) }).addTo(group);
            obstacles.push([work, 17]);
            const place = tile ? '' : String(workLabel || '').split(',')[0].trim();
            // The workplace first, then the lines' badges, then the stops' names.
            if (place) labels.unshift({ latlng: work, text: place, kind: 'work' });
        }
        labels.sort((a, b) => ['work', 'badge', 'stop'].indexOf(a.kind) - ['work', 'badge', 'stop'].indexOf(b.kind));

        // Each label beside its point where it fits, clear of the others, the ends, the stops and what floats over
        // the map (the card and buttons in `pad`); one that doesn't fit is left out at this zoom.
        const placeLabels = () => {
            labelGroup.clearLayers();
            const size = map.getSize();
            const bounds = { x: 4, y: 4, w: size.x - 8, h: size.y - 8 };
            const taken = obstacles.map(([latlng, r]) => {
                const point = map.latLngToContainerPoint(latlng);
                return { x: point.x - r, y: point.y - r, w: r * 2, h: r * 2 };
            });
            taken.push({ x: 0, y: 0, w: Math.max(0, pad.left - 24), h: size.y }, { x: 0, y: 0, w: size.x, h: Math.max(0, pad.top - 12) });
            labels.forEach((label) => {
                const kind = LABEL_KINDS[label.kind];
                const point = map.latLngToContainerPoint(label.latlng);
                const width = textWidth(label.text, kind.font) + kind.padX;
                const box = placeBox([point.x, point.y], width, kind.h, taken, bounds, kind.gap);
                if (!box) return;
                L.marker(label.latlng, {
                    pane: LABEL_PANE,
                    interactive: false,
                    keyboard: false,
                    icon: labelIcon(kind.className, label.text, `${label.style ? `${label.style};` : ''}left:${(box.x - point.x).toFixed(1)}px;top:${(box.y - point.y).toFixed(1)}px`),
                }).addTo(labelGroup);
            });
        };
        if (all.length) {
            map.fitBounds(L.latLngBounds(all), {
                paddingTopLeft: [pad.left, pad.top],
                paddingBottomRight: [pad.right, pad.bottom],
                maxZoom: 16,
                animate: false,
            });
        }
        placeLabels();
        map.on('zoomend', placeLabels);
        return () => {
            map.off('zoomend', placeLabels);
            group.remove();
            labelGroup.remove();
        };
        // A new trip or mode redraws it (fitKey: the box it's fitted into changed).
    }, [map, route, mode, home, work, workLabel, swedish, pad, fitKey, tile]);
    return null;
}

// The credit without Leaflet's prefix, as on the views' maps.
function CompactCredit() {
    const map = useMap();
    useEffect(() => {
        map.attributionControl?.setPrefix(false);
    }, [map]);
    return null;
}

// Keeps the map's size in step with its box (the popup's photos resize with the window).
function SizeWatcher({ onSize }) {
    const map = useMap();
    useEffect(() => {
        const container = map.getContainer();
        if (typeof ResizeObserver === 'undefined') return undefined;
        let last = '';
        const observer = new ResizeObserver(() => {
            const key = `${container.clientWidth}x${container.clientHeight}`;
            if (key === last) return;
            last = key;
            map.invalidateSize({ animate: false });
            onSize(key);
        });
        observer.observe(container);
        return () => observer.disconnect();
    }, [map, onSize]);
    return null;
}

// variant: 'full' (the big view and the phone panel; `touch`: pinch only) or 'tile' (the collage's small
// tile: the route on the map and nothing else, no controls; its map credit is in the big view a click away).
export default function CommuteTripMap({ route, mode, home, work, workLabel, swedish, dark, pad, touch, fallback, ariaLabel, variant = 'full' }) {
    const [failed, setFailed] = useState(false);
    const [fitKey, setFitKey] = useState('');
    const onSize = useRef((key) => setFitKey(key)).current;
    const centre = home || work || [59.33, 18.06];
    const tile = variant === 'tile';
    const still = tile;
    if (failed) return fallback;
    return (
        <div className={`trip-map-live${tile ? ' is-tile' : ''}`} role="img" aria-label={ariaLabel}>
            <MapContainer
                center={centre}
                zoom={12}
                minZoom={5}
                maxZoom={17}
                zoomControl={false}
                scrollWheelZoom={!touch && !still}
                dragging={!touch && !still}
                touchZoom={!still}
                doubleClickZoom={!still}
                boxZoom={!still}
                keyboard={false}
                attributionControl={!tile}
                className="trip-map-leaflet"
            >
                <OpenFreeMapLayer style={dark ? 'dark' : 'positron'} layers={tile ? 'base' : 'all'} onFail={() => setFailed(true)} />
                {!touch && !still && <ZoomControl position="bottomright" />}
                <CompactCredit />
                <SizeWatcher onSize={onSize} />
                <TripRoute route={route} mode={mode} home={home} work={work} workLabel={workLabel} swedish={swedish} pad={pad}
                    fitKey={fitKey} tile={tile} />
            </MapContainer>
        </div>
    );
}
